// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Rectangle } from '@shared/commons/Geometry';
import { HistoryType } from '@shared/utils/History/HistoryType';
import { ChartHistoryType, TerceraChartDrawingType } from './Utils/ChartConstants';
import { DataCacheToolType } from '@shared/commons/cache/DataCacheToolEnums';
import { Graphics, SolidBrush } from '@shared/commons/Graphics';
import { Resources } from '@shared/localizations/Resources';
export class TerceraChartUtils {
    static AbsRectangle(rect) {
        const X = rect.X;
        const Y = rect.Y;
        const R = X + rect.Width;
        const B = Y + rect.Height;
        return new Rectangle(Math.min(X, R), Math.min(Y, B), Math.abs(X - R), Math.abs(Y - B));
    }
    static DrawPointer(gr, window, priceScale, pointer, height) {
        let f = pointer.Font;
        if (!f)
            f = priceScale.settings.ScaleFont;
        const autoScaleRectH = priceScale.AutoScaleRect.Height;
        const middleH = height / 2;
        const y = pointer.yLocation !== -1 ? pointer.yLocation : window.PointsConverter.GetScreenY(pointer.priceValue);
        const scaleRect = priceScale.Rectangle;
        if (scaleRect.Width === 0) {
            return;
        }
        let x = scaleRect.X;
        const width = scaleRect.Width - 1;
        const scaleRectY = scaleRect.Y;
        const scaleRectB = scaleRectY + scaleRect.Height;
        const scaleBtnRectH = priceScale.ScaleButtonOffset;
        const scaleBtnRectOffset = scaleBtnRectH + height / 2;
        let rect = new Rectangle(x, y - middleH, width, height);
        let rectX, rectY;
        const triangleHeight = 6;
        const triangleWidth = 12;
        if (y < scaleRectB - scaleBtnRectOffset && y > scaleRectY + autoScaleRectH) {
            // Within the range
            rectX = rect.X;
            rectY = rect.Y;
            const rectWidth = rect.Width;
            const rectHeight = rect.Height;
            if (pointer.backgroundBrush) {
                gr.RoundRectangle(new Rectangle(rectX, rectY, rectWidth, rectHeight), 4, pointer.backgroundBrush);
            }
        }
        else if (y >= scaleRectB - scaleBtnRectOffset) {
            // Below the range
            rect = new Rectangle(x, scaleRectB - height - triangleHeight - scaleBtnRectH, width, height);
            rectX = rect.X;
            rectY = rect.Y;
            const rectW = rect.Width;
            const rectB = rectY + rect.Height;
            gr.beginPath();
            if (pointer.backgroundBrush)
                pointer.backgroundBrush.applySettings(gr);
            if (pointer.borderPen)
                pointer.borderPen.applySettings(gr);
            if (pointer.backgroundBrush) {
                gr.RoundRectangle(rect, 4, pointer.backgroundBrush);
            }
            x = rectX + (rectW - triangleWidth) / 2;
            gr.lineTo(x, rectB - 1);
            x += triangleWidth / 2;
            gr.lineTo(x, rectB + triangleHeight);
            x += triangleWidth / 2;
            gr.lineTo(x, rectB - 1);
            gr.closePath();
            if (pointer.backgroundBrush)
                gr.fill();
            if (pointer.borderPen)
                gr.stroke();
        }
        else if (y <= scaleRectY + autoScaleRectH) {
            // Above the range
            rect = new Rectangle(x, scaleRectY + triangleHeight + autoScaleRectH, width, height);
            rectX = rect.X;
            rectY = rect.Y;
            const rectW = rect.Width;
            gr.beginPath();
            if (pointer.backgroundBrush)
                pointer.backgroundBrush.applySettings(gr);
            if (pointer.borderPen)
                pointer.borderPen.applySettings(gr);
            if (pointer.backgroundBrush) {
                gr.RoundRectangle(rect, 4, pointer.backgroundBrush);
            }
            x = rectX + (rectW - triangleWidth) / 2;
            gr.lineTo(x, rectY + 1);
            x += triangleWidth / 2;
            gr.lineTo(x, rectY - triangleHeight);
            x += triangleWidth / 2;
            gr.lineTo(x, rectY + 1);
            gr.closePath();
            if (pointer.backgroundBrush)
                gr.fill();
            if (pointer.borderPen)
                gr.stroke();
        }
        if (pointer.backgroundBrush && pointer.formatPriceValue) {
            const testBrush = new SolidBrush(Graphics.GetContrastColor(pointer.backgroundBrush.Color));
            gr.DrawString(pointer.formatPriceValue, f, testBrush, rectX + rect.Width / 2 + 1, rectY + rect.Height / 2 + 1, 'center', 'middle');
        }
    }
    static ShiftTool(out_DataCacheTool, ww) {
        const dyy = 10;
        let vertShift = 0;
        let shiftRight = null;
        const pc = ww.PointsConverter;
        const points = out_DataCacheTool.Points;
        switch (out_DataCacheTool.ToolType) {
            case DataCacheToolType.VerticalLine:
            case DataCacheToolType.FibonacciTimeZone: {
                const ii = Math.round(pc.GetBarIndex(points[0][0]));
                if (ii + 1 < ww.i1) {
                    shiftRight = true;
                }
                else if (ii > 0) {
                    shiftRight = false;
                }
                break;
            }
            default:
                vertShift = dyy;
        }
        const len = points.length;
        for (let i = 0; i < len; i++) {
            if (shiftRight !== null) {
                let scr_X = pc.GetScreenXbyTime(points[i][0]);
                scr_X += shiftRight ? ww.XScale : -ww.XScale;
                points[i][0] = pc.GetDataX(scr_X);
            }
            const yint_Y = pc.GetScreenY(points[i][1]);
            const ydbl_Y = pc.GetDataY(yint_Y + vertShift);
            points[i][1] = ydbl_Y;
        }
    }
    // TODO. Refactor. Optimize.
    static getChartDrawingTypeLocKey(style) {
        let styleName = '';
        switch (style) {
            case TerceraChartDrawingType.Line:
                styleName = 'line';
                break;
            case TerceraChartDrawingType.Bar:
                styleName = 'bar';
                break;
            case TerceraChartDrawingType.Candle:
                styleName = 'candle';
                break;
            case TerceraChartDrawingType.Dot:
                styleName = 'dot';
                break;
            case TerceraChartDrawingType.DotLine:
                styleName = 'dotline';
                break;
            case TerceraChartDrawingType.Forest:
                styleName = 'forest';
                break;
            case TerceraChartDrawingType.Solid:
                styleName = 'solid';
                break;
            case TerceraChartDrawingType.Kagi:
                styleName = 'kagi';
                break;
        }
        return 'chart.tool.' + styleName;
    }
    static ToLocalizedTerceraChartDrawingType(type) {
        return Resources.getResource(TerceraChartUtils.getChartDrawingTypeLocKey(type));
    }
    static getChartDrawingIconByType(type) {
        switch (type) {
            case TerceraChartDrawingType.Line:
                return 'line';
            case TerceraChartDrawingType.Bar:
                return 'bar';
            case TerceraChartDrawingType.Candle:
                return 'candles';
            case TerceraChartDrawingType.Dot:
                return 'dots';
            case TerceraChartDrawingType.DotLine:
                return 'dottedLine';
            case TerceraChartDrawingType.Solid:
                return 'area';
            case TerceraChartDrawingType.Forest:
                return 'histogram';
            default:
                return '';
        }
    }
    static calculateLinearRegression(data) {
        if (data.length === 0 || data[0] === null) {
            return null;
        }
        let y = 0;
        let x = 0;
        let Sx = 0;
        let Sy = 0;
        let Sxx = 0;
        let Sxy = 0;
        let Seqiuty = 0;
        let Seqiuty2 = 0;
        const coef = {
            A: 0,
            B: 0,
            Sigma: 0
        };
        for (const d of data) {
            x = d[0];
            y = d[1];
            Sx += x;
            Sy += y;
            Sxy += x * y;
            Sxx += x * x;
            Seqiuty += y;
        }
        Sx /= data.length;
        Sy /= data.length;
        Sxy /= data.length;
        Sxx /= data.length;
        if (Sx === 0 || Sx * Sx - Sxx === 0) {
            return null;
        }
        coef.A = (Sx * Sy - Sxy) / (Sx * Sx - Sxx);
        coef.B = (Sxy - coef.A * Sxx) / Sx;
        Seqiuty /= data.length;
        for (const d of data) {
            y = d[1];
            Seqiuty2 += (y - Seqiuty) * (y - Seqiuty);
        }
        Seqiuty2 /= data.length - 1;
        coef.Sigma = Math.sqrt(Seqiuty2);
        return coef;
    }
}
// ---------------
export class TerceraChartHistoryType {
    constructor(typeEnum) {
        this.ChartDataType = typeEnum;
    }
    HistoryType() {
        return TerceraChartHistoryType.GetOriginalHistoryType(this.ChartDataType);
    }
    static GetOriginalHistoryType(ChartDataType, ins) {
        switch (ChartDataType) {
            case ChartHistoryType.Default:
                if (ins) {
                    return ins.DefaultChartHistoryType;
                }
                else {
                    return 0;
                }
            case ChartHistoryType.ByBid:
                return HistoryType.BID;
            case ChartHistoryType.ByBidAsk:
                return HistoryType.BID;
            case ChartHistoryType.ByTrades:
                return HistoryType.LAST;
            case ChartHistoryType.ByASk:
                return HistoryType.ASK;
            default:
                return 0;
        }
    }
}
